import { type ProspectDto, type ProspectTaskDto, type TaskCreateDto } from "@crowdcoursing/api/openapi";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useState } from "react";

import { TaskModal } from "~ui/molecules/TaskModal/TaskModal";

import { TaskHeader } from "./TaskHeader/TaskHeader";
import { TaskList } from "./TaskList/TaskList";
import { filterTasks, sortTasks, type TaskFilter } from "./taskUtils";

const tAddTask = "Add Task";
const tEditTask = "Edit Task";
const tDeleteTask = "Delete Task";
const tDeleteTaskConfirmation = "Are you sure you want to delete this task?";
const tTask = "Task:";
const tDeleteConfirmation = "This action cannot be undone.";
const tCancel = "Cancel";
const tDelete = "Delete";

export interface TasksTabProps {
  prospect: ProspectDto;
  taskModal: {
    isAddOpen: boolean;
    editTask: ProspectTaskDto | null;
    taskToDelete: ProspectTaskDto | null;
    deleteConfirmOpen: boolean;
    openAddModal: () => void;
    closeAddModal: () => void;
    openEditModal: (task: ProspectTaskDto) => void;
    closeEditModal: () => void;
    openDeleteModal: (task: ProspectTaskDto) => void;
    closeDeleteModal: () => void;
    handleCreateTask: (values: TaskCreateDto) => Promise<void>;
    handleUpdateTask: (values: TaskCreateDto) => Promise<void>;
    handleCompleteTask: (taskId: string) => Promise<void>;
    handleDeleteTask: () => Promise<void>;
    handleSnoozeTask: (taskId: string, task: ProspectTaskDto) => Promise<void>;
  };
}

export const TasksTab = ({ prospect, taskModal }: TasksTabProps) => {
  const [filter, setFilter] = useState<TaskFilter>("open");
  // Default to oldest first for open tasks, newest first for completed tasks
  const [sortNewestFirst, setSortNewestFirst] = useState(false);

  // Reset sort order when changing filters
  const handleFilterChange = (newFilter: TaskFilter) => {
    setFilter(newFilter);
    setSortNewestFirst(newFilter === "completed"); // true for completed, false for open/all
  };

  // Filter and sort tasks
  const filteredTasks = filterTasks(prospect.tasks ?? [], filter);
  const sortedTasks = sortTasks(filteredTasks, filter, sortNewestFirst);

  return (
    <Box display="grid" gap={3}>
      {/* Header with filters */}
      <TaskHeader
        filter={filter}
        onFilterChange={handleFilterChange}
        onAddTask={taskModal.openAddModal}
        onSortChange={() => setSortNewestFirst(!sortNewestFirst)}
        showSortButton={filter === "completed"}
        sortNewestFirst={sortNewestFirst}
      />

      {/* Task list */}
      <TaskList
        tasks={sortedTasks}
        prospect={prospect}
        filter={filter}
        onComplete={taskModal.handleCompleteTask}
        onEdit={taskModal.openEditModal}
        onSnooze={taskModal.handleSnoozeTask}
        onDelete={taskModal.openDeleteModal}
      />

      {/* Task modals */}
      <TaskModal
        open={taskModal.isAddOpen}
        onClose={taskModal.closeAddModal}
        onSubmit={taskModal.handleCreateTask}
        title={tAddTask}
      />

      <TaskModal
        open={Boolean(taskModal.editTask)}
        onClose={taskModal.closeEditModal}
        onSubmit={taskModal.handleUpdateTask}
        task={taskModal.editTask ?? undefined}
        title={tEditTask}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={taskModal.deleteConfirmOpen} onClose={taskModal.closeDeleteModal}>
        <DialogTitle>{tDeleteTask}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {tDeleteTaskConfirmation}{" "}
            {taskModal.taskToDelete?.title && (
              <>
                <br />
                <br />
                {tTask} <strong>{taskModal.taskToDelete.title}</strong>
              </>
            )}
            <br />
            <br />
            {tDeleteConfirmation}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={taskModal.closeDeleteModal}>{tCancel}</Button>
          <Button onClick={taskModal.handleDeleteTask} color="error" variant="contained">
            {tDelete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
